import '../scss/app.scss'; // Import SCSS

/**
 * Device and Browser Detection Class
 */
class DeviceTracker {
    constructor() {
        this.userAgent = navigator.userAgent;
        this.platform = navigator.platform;
    }

    getDeviceInfo() {
        const deviceInfo = {
            userAgent: this.userAgent,
            platform: this.platform,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            isMobile: this.isMobile(),
            isTablet: this.isTablet(),
            isDesktop: this.isDesktop(),
            browser: this.getBrowser(),
            os: this.getOS()
        };
        
        console.log('Device info detected:', deviceInfo);
        return deviceInfo;
    }

    isMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(this.userAgent);
    }

    isTablet() {
        return /iPad|Android/i.test(this.userAgent) && window.innerWidth >= 768;
    }

    isDesktop() {
        return !this.isMobile() && !this.isTablet();
    }

    getBrowser() {
        if (this.userAgent.indexOf('Chrome') > -1) return 'Chrome';
        if (this.userAgent.indexOf('Firefox') > -1) return 'Firefox';
        if (this.userAgent.indexOf('Safari') > -1) return 'Safari';
        if (this.userAgent.indexOf('Edge') > -1) return 'Edge';
        return 'Unknown';
    }

    getOS() {
        if (this.userAgent.indexOf('Windows') > -1) return 'Windows';
        if (this.userAgent.indexOf('Mac') > -1) return 'MacOS';
        if (this.userAgent.indexOf('Linux') > -1) return 'Linux';
        if (this.userAgent.indexOf('Android') > -1) return 'Android';
        if (this.userAgent.indexOf('iOS') > -1) return 'iOS';
        return 'Unknown';
    }
}

/**
 * Concert Data Extraction Class
 */
class ConcertDataExtractor {
    constructor(element) {
        this.element = element;
        this.pageData = this.extractPageData();
    }

    extractPageData() {
        const data = {
            concertTitle: this.getConcertTitle(),
            concertDate: this.getConcertDate(),
            venue: this.getVenue(),
            ticketUrl: this.getTicketUrl(),
            series: this.getSeries(),
            sponsors: this.getSponsors()
        };
        
        console.log('Concert data extracted:', data);
        return data;
    }

    getConcertTitle() {
        const titleElement = document.querySelector('.title h2');
        const title = titleElement ? titleElement.textContent.trim() : 'Unknown Concert';
        console.log('Concert title found:', title);
        return title;
    }

    getConcertDate() {
        const dateElement = document.querySelector('.title h3');
        const date = dateElement ? dateElement.textContent.trim() : 'Unknown Date';
        console.log('Concert date found:', date);
        return date;
    }

    getVenue() {
        // Find venue section by looking for text content
        const textBoxes = document.querySelectorAll('.text-box');
        for (let box of textBoxes) {
            const h4s = box.querySelectorAll('h4');
            for (let h4 of h4s) {
                if (h4.textContent.includes('VENUE')) {
                    const venueLink = box.querySelector('p a');
                    const venue = venueLink ? venueLink.textContent.trim() : 'Unknown Venue';
                    console.log('Venue found:', venue);
                    return venue;
                }
            }
        }
        console.log('Venue not found, returning default');
        return 'Unknown Venue';
    }

    getTicketUrl() {
        const url = this.element.href || 'No URL';
        console.log('Ticket URL found:', url);
        return url;
    }

    getSeries() {
        const seriesElement = document.querySelector('.title h4');
        const series = seriesElement ? seriesElement.textContent.trim() : 'Unknown Series';
        console.log('Series found:', series);
        return series;
    }

    getSponsors() {
        // Find sponsors section by looking for text content
        const textBoxes = document.querySelectorAll('.text-box');
        for (let box of textBoxes) {
            const h4s = box.querySelectorAll('h4');
            for (let h4 of h4s) {
                if (h4.textContent.includes('SPONSORS')) {
                    const sponsorImages = box.querySelectorAll('p img');
                    return Array.from(sponsorImages).map(img => img.alt || 'Unknown Sponsor');
                }
            }
        }
        return [];
    }
}

/**
 * Event Data Builder Class
 */
class EventBuilder {
    constructor(concertData, deviceInfo, interactionType) {
        this.concertData = concertData;
        this.deviceInfo = deviceInfo;
        this.interactionType = interactionType;
        this.timestamp = new Date().toISOString();
    }

    buildFacebookPixelEvent() {
        // Different event names for different interaction types
        const eventName = this.interactionType === 'click' ? 'Concert_Ticket_Click' : 'Concert_Ticket_Interest';
        
        return {
            event: eventName,
            eventData: {
                content_name: this.concertData.concertTitle,
                content_category: 'Concert Tickets',
                content_type: 'ticket',
                value: this.interactionType === 'click' ? 1 : 0.1, // Lower value for interest
                currency: 'USD',
                custom_data: {
                    concert_date: this.concertData.concertDate,
                    venue: this.concertData.venue,
                    series: this.concertData.series,
                    interaction_type: this.interactionType,
                    device_type: this.getDeviceType(),
                    timestamp: this.timestamp
                }
            }
        };
    }

    buildWordPressTrackingData() {
        return {
            action: 'kso_track_concert_interaction',
            nonce: window.kso_ajax_nonce || '',
            data: {
                concert_title: this.concertData.concertTitle,
                concert_date: this.concertData.concertDate,
                venue: this.concertData.venue,
                ticket_url: this.concertData.ticketUrl,
                series: this.concertData.series,
                sponsors: this.concertData.sponsors,
                interaction_type: this.interactionType,
                timestamp: this.timestamp,
                device_info: this.deviceInfo,
                page_url: window.location.href,
                referrer: document.referrer,
                user_id: window.kso_user_id || null,
                utm_data: this.getUTMParameters(),
                traffic_source: this.getTrafficSource()
            }
        };
    }

    /**
     * Extract UTM parameters from current URL
     */
    getUTMParameters() {
        const urlParams = new URLSearchParams(window.location.search);
        const utmData = {
            utm_source: urlParams.get('utm_source') || '',
            utm_medium: urlParams.get('utm_medium') || '',
            utm_campaign: urlParams.get('utm_campaign') || '',
            utm_content: urlParams.get('utm_content') || '',
            utm_term: urlParams.get('utm_term') || ''
        };
        
        console.log('Current URL:', window.location.href);
        console.log('UTM Parameters detected:', utmData);
        
        return utmData;
    }

    /**
     * Determine traffic source based on UTM params and referrer
     */
    getTrafficSource() {
        const utmData = this.getUTMParameters();
        
        console.log('Determining traffic source with UTM data:', utmData);
        
        // If we have UTM source, use that
        if (utmData.utm_source) {
            let source = utmData.utm_source;
            if (utmData.utm_medium) {
                source += ` (${utmData.utm_medium})`;
            }
            if (utmData.utm_campaign) {
                source += ` - ${utmData.utm_campaign}`;
            }
            console.log('Traffic source from UTM:', source);
            return source;
        }
        
        // Otherwise, analyze referrer
        const referrer = document.referrer;
        console.log('No UTM source, checking referrer:', referrer);
        
        if (!referrer) {
            console.log('No referrer, marking as Direct');
            return 'Direct';
        }
        
        try {
            const referrerDomain = new URL(referrer).hostname.toLowerCase();
            
            if (referrerDomain.includes('google')) return 'Google';
            if (referrerDomain.includes('facebook') || referrerDomain.includes('fb.')) return 'Facebook';
            if (referrerDomain.includes('instagram')) return 'Instagram';
            if (referrerDomain.includes('twitter') || referrerDomain.includes('t.co')) return 'Twitter';
            if (referrerDomain.includes('linkedin')) return 'LinkedIn';
            if (referrerDomain.includes('youtube')) return 'YouTube';
            if (referrerDomain.includes('bing')) return 'Bing';
            
            return referrerDomain;
        } catch (e) {
            console.log('Error parsing referrer:', e);
            return 'Unknown';
        }
    }

    getDeviceType() {
        if (this.deviceInfo.isMobile) return 'mobile';
        if (this.deviceInfo.isTablet) return 'tablet';
        return 'desktop';
    }
}

/**
 * Facebook Pixel Dispatcher Class
 */
class PixelDispatcher {
    constructor(pixelId = '2209460649570160') {
        this.pixelId = pixelId;
        this.initPixel();
    }

  initPixel() {
    if (typeof fbq === 'undefined') {
        this.loadPixelScript(); // defines fbq stub and loads script
    }

    // Initialize our pixel ID (works with stub too, will be queued)
    fbq('init', this.pixelId);

    // Fire a PageView so Meta knows the pixel is active
    fbq('track', 'PageView');
}

    loadPixelScript() {
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window,document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');

        // We no longer call fbq('init') here; it's handled in initPixel
        // so that we can consistently do init + PageView together.
    }

    sendEvent(eventData) {
        if (typeof fbq !== 'undefined') {
            fbq('track', eventData.event, eventData.eventData);
            console.log('Facebook Pixel Event Sent:', eventData);
        } else {
            console.warn('Facebook Pixel not loaded');
        }
    }
}


/**
 * WordPress AJAX Handler Class
 */
class WordPressTracker {
    constructor() {
        this.ajaxUrl = window.ajaxurl || '/wp-admin/admin-ajax.php';
    }

    sendData(data) {
        console.log('Attempting to send data to WordPress:', data);
        console.log('AJAX URL:', this.ajaxUrl);
        
        // Convert the data object to a flat structure for URLSearchParams
        const flatData = this.flattenDataForPHP(data);
        console.log('Flattened data for PHP:', flatData);
        
        return fetch(this.ajaxUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(flatData)
        })
        .then(response => {
            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);
            return response.text(); // Get text first to see what we're receiving
        })
        .then(text => {
            console.log('Raw response:', text);
            try {
                const result = JSON.parse(text);
                console.log('WordPress tracking data sent successfully:', result);
                return result;
            } catch (e) {
                console.error('Failed to parse JSON response:', e);
                console.error('Response was:', text);
                throw new Error('Invalid JSON response from server');
            }
        })
        .catch(error => {
            console.error('WordPress tracking error:', error);
            console.error('Failed to send data:', data);
        });
    }

    /**
     * Flatten nested data object for PHP URLSearchParams
     */
    flattenDataForPHP(data) {
        const flattened = {};
        
        // Copy non-nested fields directly
        for (const [key, value] of Object.entries(data)) {
            if (key === 'data') {
                // Flatten the nested data object
                const nestedData = value;
                for (const [nestedKey, nestedValue] of Object.entries(nestedData)) {
                    if (nestedKey === 'device_info' && typeof nestedValue === 'object') {
                        // Flatten device_info
                        for (const [deviceKey, deviceValue] of Object.entries(nestedValue)) {
                            flattened[`data[device_info][${deviceKey}]`] = deviceValue;
                        }
                    } else if (Array.isArray(nestedValue)) {
                        // Handle arrays (like sponsors)
                        flattened[`data[${nestedKey}]`] = JSON.stringify(nestedValue);
                    } else {
                        flattened[`data[${nestedKey}]`] = nestedValue;
                    }
                }
            } else {
                flattened[key] = value;
            }
        }
        
        return flattened;
    }
}

/**
 * DOM Handler Class
 */
class DOMHandler {
    constructor(targetClass = 'concert-cart') {
        this.targetClass = targetClass;
        this.elements = [];
        this.findElements();
    }

    findElements() {
        this.elements = document.querySelectorAll(`.${this.targetClass}`);
        console.log(`Found ${this.elements.length} elements with class: ${this.targetClass}`);
    }

    attachClickHandlers(callback) {
        this.elements.forEach((element, index) => {
            element.addEventListener('click', (event) => {
                callback(event, element, index);
            });
        });
    }

    attachPreloadHandlers(callback) {
        this.elements.forEach((element, index) => {
            element.addEventListener('mouseenter', (event) => {
                callback(event, element, index, 'hover');
            });
        });
    }
}

/**
 * Main Meta Event Tracker Class
 */
class MetaEventTracker {
    constructor(options = {}) {
        this.pixelId = options.pixelId || '2209460649570160';
        this.targetClass = options.targetClass || 'concert-cart';
        
        // Event throttling to prevent duplicates
        this.lastEventTime = {};
        this.eventThrottleMs = 1000; // 1 second between same events
        
        // Initialize helper classes
        this.deviceTracker = new DeviceTracker();
        this.pixelDispatcher = new PixelDispatcher(this.pixelId);
        this.wordpressTracker = new WordPressTracker();
        this.domHandler = new DOMHandler(this.targetClass);
        
        this.init();
    }

    init() {
        console.log('KSO Meta Event Tracker Initialized');
        
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupTracking());
        } else {
            this.setupTracking();
        }
    }

    setupTracking() {
        // Track clicks
        this.domHandler.attachClickHandlers((event, element, index) => {
            this.handleInteraction(event, element, 'click');
        });

        // Track hover intent
        this.domHandler.attachPreloadHandlers((event, element, index, type) => {
            this.handleInteraction(event, element, type);
        });

        console.log('Concert ticket tracking active');
    }

    handleInteraction(event, element, interactionType) {
        // Create a unique key for this interaction
        const elementKey = element.href + '-' + interactionType;
        const now = Date.now();
        
        // Check if we've recently tracked this same interaction
        if (this.lastEventTime[elementKey] && (now - this.lastEventTime[elementKey]) < this.eventThrottleMs) {
            console.log(`Throttling ${interactionType} event - too recent`);
            return;
        }
        
        // Update last event time
        this.lastEventTime[elementKey] = now;
        
        const deviceInfo = this.deviceTracker.getDeviceInfo();
        const concertData = new ConcertDataExtractor(element);
        const eventBuilder = new EventBuilder(concertData.pageData, deviceInfo, interactionType);

        // Send to Facebook Pixel
        const pixelEvent = eventBuilder.buildFacebookPixelEvent();
        this.pixelDispatcher.sendEvent(pixelEvent);

        // Send to WordPress (asynchronously, don't wait for response)
        const wpData = eventBuilder.buildWordPressTrackingData();
        this.wordpressTracker.sendData(wpData);

        // For clicks, track but don't interfere with navigation
        if (interactionType === 'click') {
            console.log('Concert ticket click tracked - proceeding to Ticketmaster');
            // Let the normal click behavior happen (no preventDefault)
            // The browser will naturally navigate to the href URL
        }

        // For hovers, just track (no navigation)
        if (interactionType === 'hover') {
            console.log('Concert ticket hover tracked');
        }
    }
}

// Initialize the tracker when the script loads
document.addEventListener('DOMContentLoaded', () => {
    // Set up global variables from WordPress
    window.kso_ajax_nonce = window.kso_ajax_object?.nonce || '';
    window.kso_user_id = window.kso_ajax_object?.user_id || null;
    window.ajaxurl = window.kso_ajax_object?.ajax_url || '/wp-admin/admin-ajax.php';
    
    window.ksoTracker = new MetaEventTracker();
});

console.log('KSO Meta Events Tracking Loaded');
